import React, { Component } from "react";
import type { ApolloClient } from "@apollo/client";
import Promise from "bluebird";
import {
  chunk,
  filter,
  flatten,
  get,
  includes,
  isEmpty,
  map,
  reduce,
  round,
  size,
  some,
  sum,
  transform,
} from "lodash";
import { Col, Container, Row } from "react-bootstrap";
import moment from "moment";
import PaychexKpis from "./PaychexKpis";
import {
  individualRedPathStates,
  individualYellowpathStates,
} from "statics/states";
import AccountStatusSummaryTable from "./AccountStatusSummaryTable";
import UserDataTable from "./UserDataTable";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import {
  getAllPaychexUsers,
  getPaychexUsersReport,
} from "actions/adminActions";
import IconSpinner from "components/IconSpinner";
import { createErrorSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import Alert from "components/Alert";
import type { RootState } from "types/state";
import type { GetPaychexUsersReportQuery } from "types/graphql";

// A single user report row, as returned by getPaychexUsersReport (codegen)
// with the array/null wrappers stripped. This is the element type held in
// component state after flatten + isEmpty filtering.
type PaychexUserReport = NonNullable<
  NonNullable<GetPaychexUsersReportQuery["getPaychexUsersReport"]>[number]
>;

// A single rollover, as nested under iconAccounts in the report row.
type IconAccount = NonNullable<
  NonNullable<PaychexUserReport["iconAccounts"]>[number]
>;
type Rollover = NonNullable<NonNullable<IconAccount["rollovers"]>[number]>;

const stateToCurrentTransitionText: Record<string, string> = {
  IndividualSubmitSsnPending: "Signup dropoff at skip SSN",
  IndividualEmailVerificationPending: "Signup dropoff at email verification",
  IndividualLoginCreated: "Signup dropoff at email verification",
  IndividualEmailVerificationComplete: "Signup dropoff at user info",
  IndividualApplicationInfoComplete: "Signup dropoff at disclosures",
  IndividualInvestmentInfoComplete: "Signup dropoff at portfolio selection",
  IndividualDisclosuresComplete: "Signup dropoff at investment info",
  IndividualAccountTypeSelectionPending: "Signup dropoff at account selection",
  IndividualTermsPending: "Signup dropoff at terms",
  IndividualKYC2Yellow: "Yellow",
  IndividualApplicationRequiresAdditionalDocumentation: "Yellow",
  IndividualApplicationAdditionalDocumentationSubmitted: "Yellow",
  IndividualApplicationAppealDocumentationAdminReviewPending: "Yellow",
  IndividualApplicationRequiresAdditionalDocumentationForAppeal: "Yellow",
  IndividualApplicationAppealSubmitted: "Yellow",
  IndividualApplicationAdditionalDocumentationSubmittedKYC1: "Yellow",
  IndividualApplicationRequiresAdditionalDocumentationKYC1: "Yellow",
  IndividualApplicationRequiresAdditionalDocumentationForAppealKYC1: "Yellow",
  IndividualApplicationAppealSubmittedKYC1: "Yellow",
  IndividualApplicationAppealDocumentationAdminReviewPendingKYC1: "Yellow",
  IndividualApplicationAttestationSubmitted: "Yellow",
  IndividualKYC1Red: "Red",
  IndividualKYC2Red: "Red",
  AccountApplicationDenied: "Red",
  IndividualApplicationDenied: "Red",
  IndividualCompletedForms: "Review",
  IndividualActive: "IRA open",
  IndividualClosed: "Account closed",
  IndividualKYC1Green: "KYC in Progress",
  IndividualApplicationSubmittedKYC1: "KYC in Progress",
  IndividualApplicationSubmittedKYC2: "KYC in Progress",
  IndividualApplicationApproved: "IRA open",
};

const stateMachineToDisplay: Record<string, string> = {
  ["kyc.individual"]: "INDIVIDUAL_KYC_STATE",
  ["onboarding.individual"]: "INDIVIDUAL_ON_BOARDING_STATE",
  ["lifecycle.individual"]: "INDIVIDUAL_LIFECYCLE_STATE",
};

const errorSelector = createErrorSelector(
  adminConstants.GET_PAYCHEX_USERS_REPORT
);
const mapStateToProps = (state: RootState) => ({
  error: errorSelector(state),
});

const mapDispatchToProps = {
  getPaychexUsersReport,
  getAllPaychexUsers,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface State {
  loading: boolean;
  loadingProgress: number;
  totalUsers: number;
  paychexUsersReport: PaychexUserReport[];
}

class PaychexDashboard extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      loading: true,
      loadingProgress: 0,
      totalUsers: 0,
      paychexUsersReport: [],
    };
  }

  /**
   * Process promises in batches with concurrency control
   * @param {Array} items - Items to process
   * @param {Function} processor - Async function to process each item
   * @param {Number} concurrency - Max number of concurrent requests
   */
  async processBatch<TItem, TResult>(
    items: TItem[],
    processor: (item: TItem, index: number) => Promise<TResult>,
    concurrency = 10
  ) {
    const results = await Promise.map(
      items,
      (item, index) =>
        processor(item, index).then((result) => {
          return result;
        }),
      { concurrency }
    );

    return Promise.all(results);
  }

  async componentDidMount() {
    try {
      // dispatchFullLifecycle resolves to a success | failure action union; the
      // original code reads the success `.data` shape directly — cast to it.
      const { data } = (await this.props.getAllPaychexUsers(
        this.props.client as ApolloClient<object>
      )) as unknown as { data: { userId: string }[] };

      // Split users into batches of 50 userIds
      const batchSize = 50;
      const userIdBatches = chunk(
        data.map((user) => user.userId),
        batchSize
      );

      // Process batches with concurrency limit (3 concurrent batch requests = 150 users max at once)
      const batchResponses = await this.processBatch(
        userIdBatches,
        async (userIdBatch) => {
          // same success | failure union cast as above
          return (await this.props.getPaychexUsersReport(
            this.props.client as ApolloClient<object>,
            userIdBatch
          )) as unknown as { data: PaychexUserReport[] };
        },
        3 // Process 3 batches concurrently
      );

      // Flatten and extract all user reports from batch responses
      const paychexUsersReport = flatten(
        batchResponses.map((response) => response.data || [])
      ).filter((report) => !isEmpty(report));

      this.setState({ loading: false, paychexUsersReport });
    } catch (error) {
      console.error("Error loading Paychex users:", error);
      this.setState({ loading: false });
    }
  }

  getKpiData = () => {
    const { rollovers, aum } = transform(
      this.state.paychexUsersReport,
      (acc: { rollovers: Rollover[]; aum: number }, data) => {
        const rollovers = flatten(
          map(data.iconAccounts, "rollovers")
        ) as Rollover[];
        const completedRollovers = filter(
          rollovers,
          (rollover) => rollover.status === "COMPLETE"
        );
        if (!isEmpty(completedRollovers)) {
          const totalAum = reduce(
            data.iconAccounts,
            (acc, account) => {
              const aum = get(account, "balances.totalAccountBalance", 0);
              return acc + aum;
            },
            0
          );
          acc.aum += totalAum;
        }
        acc.rollovers.push(...completedRollovers);
        return acc;
      },
      { rollovers: [] as Rollover[], aum: 0 }
    );

    const rolloverCount = rollovers.length;
    const totalRolloverAmount = rollovers.reduce(
      // rollover.amount is `number | null`; the runtime relies on null
      // coercing to 0 under `+`.
      (acc, rollover) => acc + (rollover.amount as unknown as number),
      0
    );
    const averageSize = totalRolloverAmount / rolloverCount;

    return {
      rolloverCount,
      totalRolloverAmount,
      averageSize,
      aumTotal: aum,
    };
  };

  calcAccountStatusFromState = (
    state: PaychexUserReport["userState"],
    userAuthorizations: PaychexUserReport["userAuthorizations"]
  ) => {
    if (size(userAuthorizations) === 1) {
      return "NO_LOGIN";
    }

    if (includes(individualYellowpathStates, state)) {
      return "YELLOW";
    } else if (includes(individualRedPathStates, state)) {
      return "RED";
    } else {
      return "MID_APP";
    }
  };

  getAccountStatusData = () => {
    const statusData = map(this.state.paychexUsersReport, (data) => {
      const accounts = data.iconAccounts;
      let status = "NO-LOGIN";
      let funded = false;
      // all we can rely on is the state
      if (data.userCurrentStateMachine === "lifecycle.individual") {
        status = "ACTIVE";
        funded = some(
          accounts,
          (account) => get(account, "balances.principal", 0) > 0
        );
      } else {
        status = this.calcAccountStatusFromState(
          data.userState,
          data.userAuthorizations
        );
      }
      return {
        status,
        funded,
      };
    });

    const statusCounts = reduce(
      statusData,
      (acc, { status, funded }) => {
        acc[status] = (acc[status] || 0) + 1;
        if (funded) {
          acc.FUNDED = (acc.FUNDED || 0) + 1;
        }
        return acc;
      },
      // index signature: `status` is a dynamic string key (and the
      // `calcAccountStatusFromState` "NO_LOGIN" vs initial "NO-LOGIN" mismatch
      // means extra keys can appear at runtime).
      {
        ACTIVE: 0,
        NO_LOGIN: 0,
        MID_APP: 0,
        YELLOW: 0,
        RED: 0,
        FUNDED: 0,
      } as Record<string, number>
    );

    const total = size(statusData);

    const tableData: {
      status: string;
      count: number;
      percentOfTotal?: string;
    }[] = map(statusCounts, (count, status) => ({
      status,
      count,
      percentOfTotal: `${((count / total) * 100).toFixed(1)}%`,
    }));
    tableData.push({ status: "TOTAL", count: total });
    return tableData;
  };

  getInitialAndCurrentStateTranslation = (userData: PaychexUserReport) => {
    return {
      initialStateTranslation: userData.userState,
      currentStateTranslation: userData.userState,
    };
  };

  getPaychexUsersData = () => {
    const formatDate = (
      date: moment.MomentInput,
      format = "MM/DD/YYYY HH:mm:ss"
    ) => moment(date).utc().format(format);

    const processedData: Record<string, unknown>[] = [];

    this.state.paychexUsersReport.forEach((data) => {
      let currentStateTranslation =
        stateToCurrentTransitionText[data.userState];

      if (size(data.userAuthorizations) === 1) {
        currentStateTranslation = "SSO'd over, did not create login";
      }

      let paychexRothBalance, paychexTraditionalBalance;

      // Get balances from paychexMetadata if available
      if (!isEmpty(data.paychexMetadata)) {
        // isEmpty is not a TS type guard; paychexMetadata is still typed
        // `Record<string, unknown> | null` here — narrow it.
        const { rothBalance, preTaxBalance } = data.paychexMetadata as Record<
          string,
          unknown
        >;
        paychexRothBalance = rothBalance;
        paychexTraditionalBalance = preTaxBalance;
      }

      // Process rollovers and state

      // Create base record with all details
      const baseRecord = {
        accountCreatedDate: formatDate(
          data.createdAt,
          "YYYY-MM-DD[T]HH:mm:ss.SSS[Z]"
        ),
        currentStateTranslation,
        accountEmail: data.email,
        userId: data.userId,
        stateMachine: stateMachineToDisplay[data.userCurrentStateMachine],
        accountState: data.userState,
        accountStateTrigger: data.userStateTriggerEvent,
        accountStateCreatedAt: formatDate(data.stateLastUpdated),
        paychexRothBalance,
        paychexTraditionalBalance,
        paychexMetadata: data.paychexMetadata,
      };

      // Add account type records
      if (isEmpty(data.iconAccounts)) {
        processedData.push(baseRecord);
      } else {
        // non-empty here; original code assumes each account is present.
        (data.iconAccounts as IconAccount[]).forEach((account, index) => {
          let accountRolloverStatus: string | null = null;
          let accountRolloverAmount: number | null = null;
          let accountRolloverDepositDate: string | null = null;

          // Process rollovers for this specific account
          if (size(account.rollovers) > 0) {
            accountRolloverStatus = "pending";
            // size(...) > 0 guarantees a non-null array at runtime; the
            // elements are non-null rollovers.
            const completedRollovers = (account.rollovers as Rollover[]).filter(
              (rollover) => rollover.status === "COMPLETE"
            );
            if (completedRollovers.length > 0) {
              accountRolloverStatus = "complete";
              accountRolloverAmount = round(
                sum(map(completedRollovers, "amount")),
                2
              );
              accountRolloverDepositDate = moment(
                completedRollovers[0].updatedAt
              )
                .utc()
                .format("MM/DD/YYYY");
            }
          }

          if (index === 0) {
            // First account gets all base details plus its rollover info
            processedData.push({
              ...baseRecord,
              accountType: account.accountType,
              rolloverStatus: accountRolloverStatus,
              rolloverAmount: accountRolloverAmount,
              rolloverDepositDate: accountRolloverDepositDate,
            });
          } else {
            // Additional accounts get minimal details plus their rollover info
            processedData.push({
              accountType: account.accountType,
              userId: data.userId,
              accountEmail: data.email,
              rolloverStatus: accountRolloverStatus,
              rolloverAmount: accountRolloverAmount,
              rolloverDepositDate: accountRolloverDepositDate,
            });
          }
        });
      }
    });

    return processedData;
  };

  render() {
    if (this.state.loading) {
      return (
        <Container fluid className="p-4 text-center">
          <IconSpinner centered />
        </Container>
      );
    }

    if (this.props.error) {
      return <Alert type="error" msg={this.props.error} />;
    }

    const accountStatusData = this.getAccountStatusData();

    return (
      <Container fluid className="p-4">
        <PaychexKpis kpiData={this.getKpiData()} />

        <Row className="mb-4">
          <Col md={6}>
            <AccountStatusSummaryTable
              // our rows carry `status: string` (dynamic reduce keys); the
              // table's prop type narrows status to a StatusKey literal union.
              accountStatusData={
                accountStatusData as React.ComponentProps<
                  typeof AccountStatusSummaryTable
                >["accountStatusData"]
              }
            />
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <UserDataTable
              paychexUsersData={this.getPaychexUsersData()}
              totalPaychexUsers={size(this.state.paychexUsersReport)}
            />
          </Col>
        </Row>
      </Container>
    );
  }
}

export default connector(withApollo<Props>(PaychexDashboard));
